import { Link } from "react-router-dom";
import { ChevronRight, Bookmark, Sparkles } from "lucide-react";
import { SeverityBadge } from "./SeverityBadge";
import { CategoryBadge } from "./CategoryBadge";
import type { Issue } from "../types";

interface IssueCardProps {
  issue: Issue;
  isBookmarked?: boolean;
  onToggleBookmark?: () => void;
}

export function IssueCard({
  issue,
  isBookmarked = false,
  onToggleBookmark,
}: IssueCardProps) {
  return (
    <div className="group relative overflow-hidden rounded-[24px] border border-white/10 bg-gradient-to-br from-[#091224] via-[#0b1530] to-[#080d18] p-5 transition duration-300 hover:-translate-y-1 hover:border-cyan-300/25 hover:bg-white/[0.055] hover:shadow-[0_0_24px_rgba(34,211,238,0.08)]">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(34,211,238,0.10),transparent_30%),radial-gradient(circle_at_bottom_left,rgba(217,70,239,0.06),transparent_30%)]" />

      <div className="relative z-10 flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <CategoryBadge category={issue.category} />
            <SeverityBadge severity={issue.severity} size="sm" />
          </div>

          <Link to={`/issues/${issue.id}`} className="block">
            <h3 className="text-xl font-extrabold leading-tight tracking-tight text-white transition group-hover:text-cyan-200">
              {issue.title}
            </h3>
          </Link>

          <p className="mt-3 line-clamp-3 text-sm leading-6 text-white/68">
            {issue.whatItLooksLike}
          </p>

          <div className="mt-4 rounded-2xl border border-cyan-300/12 bg-black/15 p-3">
            <div className="mb-2 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-300">
              <Sparkles className="h-3.5 w-3.5" />
              Quick Look
            </div>

            <p className="text-sm leading-6 text-white/72">
              Tap to inspect likely causes, checks, and recommended fixes.
            </p>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {issue.tags.slice(0, 4).map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-white/10 bg-black/15 px-3 py-1 text-xs text-white/60"
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-2">
          {onToggleBookmark ? (
            <button
              onClick={(e) => {
                e.preventDefault();
                onToggleBookmark();
              }}
              className={`inline-flex h-11 w-11 items-center justify-center rounded-2xl border transition ${
                isBookmarked
                  ? "border-cyan-300/25 bg-cyan-400/10 text-cyan-300"
                  : "border-white/10 bg-white/5 text-white/60 hover:border-cyan-300/20 hover:bg-white/10 hover:text-white"
              }`}
              aria-label={isBookmarked ? "Remove bookmark" : "Add bookmark"}
            >
              <Bookmark
                className={`h-5 w-5 ${isBookmarked ? "fill-current" : ""}`}
              />
            </button>
          ) : null}

          <Link
            to={`/issues/${issue.id}`}
            className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-white/60 transition hover:border-cyan-300/20 hover:bg-white/10 hover:text-cyan-300"
            aria-label={`Open ${issue.title}`}
          >
            <ChevronRight className="h-5 w-5 transition group-hover:translate-x-0.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
